/**
 * オフラインストレージサービス
 *
 * IndexedDBを使用してオフライン時の検査結果を一時保存する。
 * オンライン復帰時にsyncServiceを通じてサーバーへ同期される。
 *
 * Dexie.jsをラッパーとして使用し、IndexedDBを操作する。
 */

import Dexie, { type Table } from 'dexie';
import type { CreatePatientRequest, DeviceInfo, ExamSettings } from '../types';

/**
 * 同期待ち検査データの型
 *
 * ExamResultPageのhandleSaveで構築されるrequestBodyと同じ構造に
 * IndexedDB用のautoIncrementキーを追加したもの。
 */
export interface PendingExam {
  /** IndexedDB自動採番キー */
  id?: number;
  /** 患者ID */
  patientId: string;
  /** ローカルID（重複保存防止用） */
  localId: string;
  /** 検査設定 */
  settings: ExamSettings;
  /** 試行結果 */
  trials: {
    trialNumber: number;
    lineLengthMm: number;
    linePositionXMm: number;
    linePositionYMm: number;
    markedPositionXMm: number;
    markedPositionYMm: number;
    deviationMm: number;
    deviationPercent: number;
    isAbnormal: boolean;
    capturedImage?: string;
  }[];
  /** 検査実施日時（ISO文字列） */
  createdAt: string;
  /** 端末情報 */
  deviceInfo?: DeviceInfo;
  /** IndexedDBに保存した日時（ISO文字列） */
  savedAt: string;
  /** 恒久的な同期失敗として扱う場合の日時 */
  failedAt?: string;
  /** 恒久的な同期失敗のHTTPステータス */
  syncErrorStatus?: number;
  /** 恒久的な同期失敗の理由 */
  syncError?: string;
}

export interface OfflineExamSettingsRecord {
  key: 'latest-custom-exam-settings';
  settings: ExamSettings;
  updatedAt: string;
}

export interface PendingPresetSync {
  id?: number;
  action: 'create' | 'update';
  presetId?: string;
  payload: {
    name: string;
    settings: ExamSettings;
    isDefault: boolean;
  };
  savedAt: string;
}

export interface PendingPatientCreate {
  id?: number;
  tempId: string;
  payload: CreatePatientRequest;
  savedAt: string;
}

/**
 * Dexieデータベースクラス
 */
class OfflineDatabase extends Dexie {
  pendingExams!: Table<PendingExam, number>;
  examSettings!: Table<OfflineExamSettingsRecord, string>;
  pendingPresetSyncs!: Table<PendingPresetSync, number>;
  pendingPatientCreates!: Table<PendingPatientCreate, number>;

  constructor() {
    super('linebisect-offline');

    this.version(1).stores({
      // id: autoIncrement, localIdにインデックスを張る（重複チェック用）
      pendingExams: '++id, localId',
    });

    this.version(2).stores({
      pendingExams: '++id, localId',
      examSettings: 'key, updatedAt',
    });

    this.version(3).stores({
      pendingExams: '++id, localId',
      examSettings: 'key, updatedAt',
      pendingPresetSyncs: '++id, action, presetId, savedAt',
    });

    this.version(4).stores({
      pendingExams: '++id, localId',
      examSettings: 'key, updatedAt',
      pendingPresetSyncs: '++id, action, presetId, savedAt',
      pendingPatientCreates: '++id, tempId, savedAt',
    });
  }
}

/** データベースのシングルトンインスタンス */
const db = new OfflineDatabase();

/**
 * オフラインストレージ操作
 */
export const offlineStorage = {
  /**
   * 検査結果をIndexedDBに保存する
   *
   * @param exam - 保存する検査データ（idフィールドは自動採番）
   * @returns 保存されたレコードのID
   */
  async save(exam: Omit<PendingExam, 'id' | 'savedAt'>): Promise<number> {
    const record: PendingExam = {
      ...exam,
      savedAt: new Date().toISOString(),
    };
    return await db.pendingExams.add(record);
  },

  /**
   * 同期待ちの検査結果を全件取得する
   *
   * @returns 保存されている全ての同期待ちデータ
   */
  async getAll(): Promise<PendingExam[]> {
    return await db.pendingExams.toArray();
  },

  /**
   * 自動同期対象の検査データを取得する。
   */
  async getPendingExamsForSync(): Promise<PendingExam[]> {
    return await db.pendingExams
      .toCollection()
      .filter((exam) => !exam.failedAt)
      .toArray();
  },

  /**
   * 同期済みの検査結果をIndexedDBから削除する
   *
   * @param id - 削除するレコードのID
   */
  async delete(id: number): Promise<void> {
    await db.pendingExams.delete(id);
  },

  async markExamSyncFailed(id: number, status: number, message: string): Promise<void> {
    await db.pendingExams.update(id, {
      failedAt: new Date().toISOString(),
      syncErrorStatus: status,
      syncError: message,
    });
  },

  /**
   * オフライン仮患者IDを、サーバー同期後の正式患者IDへ置き換える
   */
  async replacePendingExamPatientId(tempPatientId: string, patientId: string): Promise<void> {
    await db.transaction('rw', db.pendingExams, async () => {
      // pendingExams は patientId インデックスを持たないため、全件走査で安全に置換する。
      await db.pendingExams
        .toCollection()
        .filter((exam) => exam.patientId === tempPatientId)
        .modify({ patientId });
    });
  },

  /**
   * 同期待ちデータの件数を取得する
   *
   * @returns 件数
   */
  async count(): Promise<number> {
    return await db.pendingExams.toCollection().filter((exam) => !exam.failedAt).count();
  },

  /**
   * 直近のカスタム検査設定を保存する（オフライン起動時の復元用）
   */
  async saveLatestExamSettings(settings: ExamSettings): Promise<void> {
    await db.examSettings.put({
      key: 'latest-custom-exam-settings',
      settings,
      updatedAt: new Date().toISOString(),
    });
  },

  /**
   * 直近のカスタム検査設定を取得する
   */
  async getLatestExamSettings(): Promise<ExamSettings | null> {
    const record = await db.examSettings.get('latest-custom-exam-settings');
    return record?.settings ?? null;
  },

  async savePendingPresetSync(
    item: Omit<PendingPresetSync, 'id' | 'savedAt'>
  ): Promise<number> {
    return await db.transaction('rw', db.pendingPresetSyncs, async () => {
      if (item.action === 'update' && item.presetId) {
        await db.pendingPresetSyncs
          .where('presetId')
          .equals(item.presetId)
          .delete();
      }

      return await db.pendingPresetSyncs.add({
        ...item,
        savedAt: new Date().toISOString(),
      });
    });
  },

  async getAllPendingPresetSyncs(): Promise<PendingPresetSync[]> {
    return await db.pendingPresetSyncs.toArray();
  },

  async deletePendingPresetSync(id: number): Promise<void> {
    await db.pendingPresetSyncs.delete(id);
  },

  async countPendingPresetSyncs(): Promise<number> {
    return await db.pendingPresetSyncs.count();
  },

  async savePendingPatientCreate(
    item: Omit<PendingPatientCreate, 'id' | 'savedAt'>
  ): Promise<number> {
    return await db.pendingPatientCreates.add({
      ...item,
      savedAt: new Date().toISOString(),
    });
  },

  async getAllPendingPatientCreates(): Promise<PendingPatientCreate[]> {
    return await db.pendingPatientCreates.toArray();
  },

  async deletePendingPatientCreate(id: number): Promise<void> {
    await db.pendingPatientCreates.delete(id);
  },

  async deletePendingPatientCreateByTempId(tempId: string): Promise<void> {
    const item = await db.pendingPatientCreates.where('tempId').equals(tempId).first();
    if (item?.id !== undefined) {
      await db.pendingPatientCreates.delete(item.id);
    }
  },

  async countPendingPatientCreates(): Promise<number> {
    return await db.pendingPatientCreates.count();
  },

  async countAllPending(): Promise<number> {
    const [examCount, presetCount, patientCreateCount] = await Promise.all([
      db.pendingExams.toCollection().filter((exam) => !exam.failedAt).count(),
      db.pendingPresetSyncs.count(),
      db.pendingPatientCreates.count(),
    ]);
    return examCount + presetCount + patientCreateCount;
  },
};

export default offlineStorage;
